import React from 'react'

/** 공통: 제품 목업용 래퍼 (장식 SVG — 스크린리더는 섹션 제목으로 이해) */
function MockFrame({ children, className = '', ariaLabel }) {
  return (
    <div className={className} role="img" aria-label={ariaLabel}>
      {children}
    </div>
  )
}

/** `public/sgez-landing-assets/` 에 PNG가 있으면 사용 (npm run capture:landing-assets) */
function landingAssetSrc(filename) {
  const base = typeof process.env.PUBLIC_URL === 'string' ? process.env.PUBLIC_URL.replace(/\/$/, '') : ''
  return `${base}/sgez-landing-assets/${filename}`
}

export function LandingFlowHeroImage({ className = '', alt = '' } = {}) {
  return (
    <img
      src={landingAssetSrc('hero-flow-v2.png')}
      alt={alt}
      className={className}
      loading="eager"
      decoding="async"
    />
  )
}

export function WorkflowFlowImage({ className = '', alt = '' } = {}) {
  return (
    <img
      src={landingAssetSrc('workflow-flow-v1.png')}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
    />
  )
}

/**
 * 마케팅 히어로: 실제 관리자 캡처 대신 업무 흐름을 압축한 합성 대시보드.
 * CollectUI의 B2B dashboard landing 계열처럼 제품의 "운영 장면"을 먼저 보여준다.
 *
 * @param {{ tone?: 'light' | 'dark' }} props
 */
export function LandingCommandCenterMockup({ tone = 'dark' } = {}) {
  const dark = tone === 'dark'
  const suffix = dark ? 'dark' : 'light'
  const frameClass = dark
    ? 'overflow-hidden rounded-lg border border-white/10 bg-slate-950 shadow-2xl shadow-slate-950/40 ring-1 ring-white/10'
    : 'overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_28px_80px_-34px_rgba(15,23,42,0.24)] ring-1 ring-slate-100'
  const bg = dark ? '#07111f' : '#f8fafc'
  const panel = dark ? '#0f1b2d' : '#ffffff'
  const panel2 = dark ? '#111f34' : '#f1f5f9'
  const border = dark ? '#26364f' : '#dbe4ee'
  const muted = dark ? '#94a3b8' : '#64748b'
  const text = dark ? '#e2e8f0' : '#0f172a'
  const faint = dark ? '#172338' : '#eef2f7'
  const accent = dark ? '#38bdf8' : '#2563eb'
  const mint = dark ? '#2dd4bf' : '#0f766e'
  const amber = dark ? '#fbbf24' : '#b45309'

  const flowSteps = [
    { x: 64, y: 92, label: '고객 인입', value: '42', color: accent },
    { x: 222, y: 92, label: '진료 분석', value: '18', color: mint },
    { x: 380, y: 92, label: '설계 의뢰', value: '12', color: '#60a5fa' },
    { x: 538, y: 92, label: '자동 배정', value: '9', color: amber },
  ]

  const queueRows = [
    ['김○○', '진료정보 확인', '분석 완료'],
    ['박○○', '설계 의뢰', '배정 대기'],
    ['이○○', '보완 요청', '진행 중'],
  ]

  return (
    <MockFrame ariaLabel="고객 인입부터 진료정보 분석, 설계 의뢰, 자동 배정까지 이어지는 설계EZ 업무 흐름 목업" className={frameClass}>
      <svg viewBox="0 0 760 560" className="h-auto w-full" aria-hidden="true">
        <defs>
          <linearGradient id={`sgez-command-surface-${suffix}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={dark ? '#0b1628' : '#ffffff'} />
            <stop offset="100%" stopColor={dark ? '#07111f' : '#eef6ff'} />
          </linearGradient>
          <linearGradient id={`sgez-command-line-${suffix}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={accent} stopOpacity="0.95" />
            <stop offset="100%" stopColor={mint} stopOpacity="0.95" />
          </linearGradient>
          <filter id={`sgez-command-shadow-${suffix}`} x="-18%" y="-18%" width="136%" height="136%">
            <feDropShadow dx="0" dy="14" stdDeviation="16" floodColor="#020617" floodOpacity={dark ? '0.34' : '0.12'} />
          </filter>
        </defs>

        <rect width="760" height="560" fill={bg} />
        <rect x="24" y="24" width="712" height="512" rx="18" fill={`url(#sgez-command-surface-${suffix})`} stroke={border} />

        <g>
          <rect x="48" y="48" width="664" height="46" rx="10" fill={dark ? '#0b1424' : '#ffffff'} stroke={border} />
          <rect x="68" y="64" width="84" height="14" rx="7" fill={accent} opacity={dark ? '0.24' : '0.14'} />
          <text x="78" y="75" fill={text} fontSize="11" fontWeight="800">설계EZ 운영 흐름</text>
          <rect x="576" y="61" width="48" height="18" rx="9" fill={dark ? '#12233b' : '#eff6ff'} stroke={dark ? '#1e3a5f' : '#bfdbfe'} />
          <text x="600" y="74" textAnchor="middle" fill={accent} fontSize="9" fontWeight="800">LIVE</text>
          <rect x="636" y="61" width="76" height="18" rx="9" fill={dark ? '#122c2a' : '#ecfdf5'} stroke={dark ? '#1f4d47' : '#bbf7d0'} />
          <text x="674" y="74" textAnchor="middle" fill={mint} fontSize="9" fontWeight="800">자동 배정</text>
        </g>

        <g filter={`url(#sgez-command-shadow-${suffix})`}>
          <rect x="48" y="118" width="664" height="144" rx="14" fill={panel} stroke={border} />
          <text x="68" y="145" fill={text} fontSize="13" fontWeight="900">상담 전 검토 파이프라인</text>
          <text x="68" y="166" fill={muted} fontSize="10" fontWeight="600">상담 준비, 분석, 의뢰, 배정 상태를 같은 흐름에서 확인</text>

          {flowSteps.map((step, index) => (
            <g key={step.label}>
              <rect x={step.x} y="184" width="112" height="54" rx="10" fill={panel2} stroke={border} />
              <text x={step.x + 14} y="205" fill={muted} fontSize="9" fontWeight="800">{step.label}</text>
              <text x={step.x + 14} y="228" fill={step.color} fontSize="24" fontWeight="900">{step.value}</text>
              {index < flowSteps.length - 1 ? (
                <g>
                  <path
                    d={`M ${step.x + 118} 211 H ${step.x + 150}`}
                    stroke={`url(#sgez-command-line-${suffix})`}
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <path
                    d={`M ${step.x + 144} 204 L ${step.x + 152} 211 L ${step.x + 144} 218`}
                    stroke={`url(#sgez-command-line-${suffix})`}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </g>
              ) : null}
            </g>
          ))}
        </g>

        <g filter={`url(#sgez-command-shadow-${suffix})`}>
          <rect x="48" y="286" width="300" height="216" rx="14" fill={panel} stroke={border} />
          <text x="68" y="316" fill={text} fontSize="13" fontWeight="900">고객별 진행 큐</text>
          <text x="68" y="336" fill={muted} fontSize="10" fontWeight="600">누락되기 쉬운 상태를 담당자 기준으로 정리</text>
          {queueRows.map(([name, task, status], index) => (
            <g key={name}>
              <rect x="68" y={358 + index * 46} width="260" height="34" rx="9" fill={index === 1 ? faint : panel2} stroke={border} />
              <circle cx="86" cy={375 + index * 46} r="10" fill={index === 0 ? '#2563eb' : index === 1 ? '#14b8a6' : '#f59e0b'} opacity={dark ? '0.95' : '0.85'} />
              <text x="106" y={371 + index * 46} fill={text} fontSize="10" fontWeight="800">{name}</text>
              <text x="106" y={384 + index * 46} fill={muted} fontSize="9" fontWeight="600">{task}</text>
              <rect x="248" y={366 + index * 46} width="70" height="18" rx="9" fill={dark ? '#0b1424' : '#ffffff'} stroke={border} />
              <text x="283" y={379 + index * 46} textAnchor="middle" fill={index === 0 ? mint : index === 1 ? accent : amber} fontSize="8" fontWeight="900">{status}</text>
            </g>
          ))}
        </g>

        <g filter={`url(#sgez-command-shadow-${suffix})`}>
          <rect x="376" y="286" width="336" height="216" rx="14" fill={panel} stroke={border} />
          <text x="396" y="316" fill={text} fontSize="13" fontWeight="900">분석 리포트 요약</text>
          <text x="396" y="336" fill={muted} fontSize="10" fontWeight="600">고지 포인트와 설계 의뢰 근거를 바로 전달</text>
          <rect x="396" y="358" width="136" height="86" rx="10" fill={panel2} stroke={border} />
          <text x="414" y="382" fill={muted} fontSize="9" fontWeight="800">진료정보</text>
          <rect x="414" y="398" width="92" height="7" rx="3.5" fill={accent} opacity={dark ? '0.48' : '0.32'} />
          <rect x="414" y="414" width="68" height="7" rx="3.5" fill={dark ? '#475569' : '#cbd5e1'} />
          <rect x="414" y="428" width="96" height="7" rx="3.5" fill={dark ? '#475569' : '#cbd5e1'} />
          <rect x="548" y="358" width="144" height="86" rx="10" fill={panel2} stroke={border} />
          <text x="566" y="382" fill={muted} fontSize="9" fontWeight="800">추천 근거</text>
          <rect x="566" y="398" width="48" height="22" rx="8" fill={dark ? '#12393a' : '#ccfbf1'} stroke={dark ? '#1f766d' : '#5eead4'} />
          <text x="590" y="413" textAnchor="middle" fill={mint} fontSize="8" fontWeight="900">후보 A</text>
          <rect x="622" y="398" width="48" height="22" rx="8" fill={dark ? '#13284a' : '#dbeafe'} stroke={dark ? '#1d4ed8' : '#93c5fd'} />
          <text x="646" y="413" textAnchor="middle" fill={accent} fontSize="8" fontWeight="900">후보 B</text>
          <rect x="396" y="462" width="296" height="22" rx="11" fill={dark ? '#12233b' : '#eff6ff'} stroke={dark ? '#1e3a5f' : '#bfdbfe'} />
          <rect x="396" y="462" width="202" height="22" rx="11" fill={`url(#sgez-command-line-${suffix})`} opacity={dark ? '0.72' : '0.82'} />
          <text x="414" y="477" fill={dark ? '#e0f2fe' : '#ffffff'} fontSize="9" fontWeight="900">분석 완료 후 의뢰 전환</text>
        </g>
      </svg>
    </MockFrame>
  )
}

const HERO_MOCK_FRAME_CLASS_DARK =
  'overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.04] p-2 shadow-2xl shadow-black/40 ring-1 ring-white/10 backdrop-blur-sm transition-all duration-500 motion-safe:hover:-translate-y-1 motion-safe:hover:shadow-[0_28px_80px_-20px_rgba(56,189,248,0.28)] motion-safe:hover:ring-sky-400/25 sm:p-3'

const HERO_MOCK_FRAME_CLASS_LIGHT =
  'overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-2 shadow-[0_24px_80px_-28px_rgba(15,23,42,0.14)] ring-1 ring-slate-100 transition-all duration-500 motion-safe:hover:-translate-y-1 motion-safe:hover:shadow-[0_32px_90px_-28px_rgba(56,189,248,0.18)] motion-safe:hover:ring-sky-200/80 sm:rounded-[28px] sm:p-3'

/**
 * 히어로: 웹 대시보드 + 모바일 + 플로팅 단계 칩
 * — `hero-main.png` 가 있으면 실제 캡처, 없거나 로드 실패 시 SVG 목업
 * @param {{ tone?: 'light' | 'dark' }} props
 */
export function HeroProductMockup({ tone = 'dark' } = {}) {
  const [rasterFailed, setRasterFailed] = React.useState(false)
  const frameClass = tone === 'light' ? HERO_MOCK_FRAME_CLASS_LIGHT : HERO_MOCK_FRAME_CLASS_DARK
  if (!rasterFailed) {
    return (
      <MockFrame
        ariaLabel="설계EZ 웹 대시보드와 모바일 화면, 의뢰 단계 안내가 함께 표현된 제품 목업"
        className={frameClass}
      >
        <img
          src={landingAssetSrc('hero-main.png')}
          alt=""
          className="h-auto w-full object-cover object-top"
          onError={() => setRasterFailed(true)}
          loading="lazy"
          decoding="async"
        />
      </MockFrame>
    )
  }
  if (tone === 'light') {
    return (
      <MockFrame
        ariaLabel="설계EZ 웹 대시보드와 모바일 화면, 의뢰 단계 안내가 함께 표현된 제품 목업"
        className={frameClass}
      >
        <svg viewBox="0 0 720 560" className="h-auto w-full" aria-hidden="true">
          <defs>
            <linearGradient id="sgez-hero-light-glow" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.2" />
            </linearGradient>
            <filter id="sgez-hero-light-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="10" stdDeviation="16" floodColor="#0f172a" floodOpacity="0.08" />
            </filter>
          </defs>
          <rect width="720" height="560" rx="24" fill="#f8fafc" />
          <circle cx="110" cy="70" r="64" fill="#38bdf8" opacity="0.08" />
          <circle cx="620" cy="430" r="96" fill="#0ea5e9" opacity="0.06" />

          <g filter="url(#sgez-hero-light-shadow)">
            <rect x="36" y="56" width="420" height="320" rx="18" fill="#ffffff" stroke="#e2e8f0" strokeWidth="1.5" />
            <rect x="36" y="56" width="420" height="34" rx="18" fill="#f1f5f9" />
            <rect x="36" y="78" width="420" height="12" fill="#f1f5f9" />
            <circle cx="54" cy="72" r="5" fill="#fca5a5" opacity="0.9" />
            <circle cx="72" cy="72" r="5" fill="#fcd34d" opacity="0.9" />
            <circle cx="90" cy="72" r="5" fill="#86efac" opacity="0.9" />
            <rect x="118" y="66" width="280" height="14" rx="7" fill="#e2e8f0" />
            <text x="126" y="77" fill="#64748b" fontSize="11" fontWeight="600">
              app.sgez.co.kr
            </text>

            <rect x="52" y="108" width="96" height="252" rx="12" fill="#f8fafc" stroke="#e2e8f0" />
            <rect x="64" y="124" width="72" height="8" rx="4" fill="#bae6fd" opacity="0.6" />
            <rect x="64" y="142" width="56" height="6" rx="3" fill="#cbd5e1" />
            <rect x="64" y="156" width="64" height="6" rx="3" fill="#cbd5e1" />
            <rect x="64" y="178" width="72" height="28" rx="8" fill="#ffffff" stroke="#bae6fd" strokeWidth="1.2" />
            <text x="72" y="196" fill="#0f172a" fontSize="10" fontWeight="700">
              의뢰
            </text>

            <rect x="160" y="108" width="276" height="120" rx="14" fill="#ffffff" stroke="#e2e8f0" />
            <text x="176" y="132" fill="#0f172a" fontSize="12" fontWeight="700">
              의뢰 현황
            </text>
            <rect x="176" y="144" width="72" height="64" rx="12" fill="#f8fafc" stroke="#e2e8f0" />
            <text x="188" y="168" fill="#64748b" fontSize="9">
              진행
            </text>
            <text x="188" y="192" fill="#0284c7" fontSize="22" fontWeight="800">
              128
            </text>
            <rect x="258" y="144" width="72" height="64" rx="12" fill="#f8fafc" stroke="#e2e8f0" />
            <text x="270" y="168" fill="#64748b" fontSize="9">
              배정
            </text>
            <text x="270" y="192" fill="#0d9488" fontSize="20" fontWeight="800">
              92%
            </text>
            <rect x="340" y="144" width="72" height="64" rx="12" fill="#f8fafc" stroke="#e2e8f0" />
            <text x="352" y="168" fill="#64748b" fontSize="9">
              알림
            </text>
            <text x="358" y="192" fill="#0369a1" fontSize="20" fontWeight="800">
              24
            </text>

            <rect x="160" y="240" width="276" height="104" rx="14" fill="#f1f5f9" stroke="#e2e8f0" />
            <text x="176" y="264" fill="#0f172a" fontSize="11" fontWeight="700">
              진행 단계
            </text>
            <rect x="176" y="276" width="52" height="14" rx="7" fill="#bae6fd" />
            <rect x="236" y="276" width="52" height="14" rx="7" fill="#7dd3fc" />
            <rect x="296" y="276" width="52" height="14" rx="7" fill="#5eead4" />
            <rect x="356" y="276" width="52" height="14" rx="7" fill="#a5f3fc" opacity="0.9" />
            <text x="176" y="318" fill="#64748b" fontSize="9">
              생성 → 배정 → 소통 → 완료
            </text>
          </g>

          {[
            { x: 468, y: 48, w: 118, h: 36, t: '의뢰 생성' },
            { x: 520, y: 100, w: 118, h: 36, t: '자동 배정' },
            { x: 488, y: 168, w: 130, h: 36, t: '진행·소통' },
            { x: 540, y: 232, w: 130, h: 36, t: '완료·보관' },
          ].map((c) => (
            <g key={c.t}>
              <rect x={c.x} y={c.y} width={c.w} height={c.h} rx="18" fill="#ffffff" stroke="#7dd3fc" strokeWidth="1.2" />
              <text x={c.x + c.w / 2} y={c.y + 23} textAnchor="middle" fill="#0f172a" fontSize="13" fontWeight="800">
                {c.t}
              </text>
            </g>
          ))}

          <g filter="url(#sgez-hero-light-shadow)">
            <rect x="548" y="300" width="124" height="220" rx="22" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.5" />
            <rect x="564" y="316" width="92" height="14" rx="7" fill="#e2e8f0" />
            <rect x="568" y="344" width="84" height="36" rx="10" fill="#f8fafc" stroke="#e2e8f0" />
            <rect x="576" y="352" width="40" height="6" rx="3" fill="#38bdf8" opacity="0.25" />
            <rect x="576" y="364" width="56" height="5" rx="2" fill="#94a3b8" />
            <rect x="568" y="390" width="84" height="36" rx="10" fill="#f8fafc" stroke="#e2e8f0" />
            <rect x="576" y="398" width="48" height="6" rx="3" fill="#2dd4bf" opacity="0.22" />
            <rect x="576" y="410" width="52" height="5" rx="2" fill="#94a3b8" />
            <text x="610" y="332" textAnchor="middle" fill="#64748b" fontSize="9" fontWeight="700">
              모바일
            </text>
            <circle cx="610" cy="472" r="16" fill="#38bdf8" opacity="0.15" />
            <path
              d="M604 472 L609 477 L620 464"
              stroke="#0284c7"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>

          <path
            d="M 456 200 Q 500 180, 520 140"
            stroke="url(#sgez-hero-light-glow)"
            strokeWidth="2.2"
            fill="none"
            opacity="0.65"
          />
          <path
            d="M 456 260 Q 510 280, 548 340"
            stroke="url(#sgez-hero-light-glow)"
            strokeWidth="2.2"
            fill="none"
            opacity="0.55"
          />
        </svg>
      </MockFrame>
    )
  }
  return (
    <MockFrame
      ariaLabel="설계EZ 웹 대시보드와 모바일 화면, 의뢰 단계 안내가 함께 표현된 제품 목업"
      className={frameClass}
    >
      <svg viewBox="0 0 720 560" className="h-auto w-full" aria-hidden="true">
        <defs>
          <linearGradient id="sgez-hero-glow" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#2dd4bf" stopOpacity="0.85" />
          </linearGradient>
          <linearGradient id="sgez-hero-panel" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#13213e" />
            <stop offset="100%" stopColor="#0f172a" />
          </linearGradient>
          <filter id="sgez-hero-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#000" floodOpacity="0.35" />
          </filter>
        </defs>

        <rect width="720" height="560" rx="24" fill="url(#sgez-hero-panel)" />
        <circle cx="120" cy="72" r="72" fill="#38bdf8" opacity="0.06" />
        <circle cx="640" cy="420" r="100" fill="#2dd4bf" opacity="0.06" />

        {/* 웹 브라우저 프레임 */}
        <g filter="url(#sgez-hero-shadow)">
          <rect x="36" y="56" width="420" height="320" rx="20" fill="#0b1220" stroke="#334155" strokeWidth="1.5" />
          <rect x="36" y="56" width="420" height="36" rx="20" fill="#0f1a2f" />
          <rect x="36" y="80" width="420" height="12" fill="#0f1a2f" />
          <circle cx="56" cy="74" r="5" fill="#ef4444" opacity="0.85" />
          <circle cx="74" cy="74" r="5" fill="#eab308" opacity="0.85" />
          <circle cx="92" cy="74" r="5" fill="#22c55e" opacity="0.85" />
          <rect x="120" y="68" width="280" height="14" rx="7" fill="#1e293b" />
          <text x="128" y="78" fill="#94a3b8" fontSize="11" fontWeight="600">
            app.sgez.co.kr
          </text>

          <rect x="52" y="108" width="96" height="252" rx="12" fill="#0c1424" stroke="#1e293b" />
          <rect x="64" y="124" width="72" height="8" rx="4" fill="#1e40af" opacity="0.5" />
          <rect x="64" y="142" width="56" height="6" rx="3" fill="#334155" />
          <rect x="64" y="156" width="64" height="6" rx="3" fill="#334155" />
          <rect x="64" y="178" width="72" height="28" rx="8" fill="#111c31" stroke="#1e3a8a" strokeOpacity="0.5" />
          <text x="72" y="196" fill="#e2e8f0" fontSize="10" fontWeight="700">
            의뢰
          </text>

          <rect x="160" y="108" width="276" height="120" rx="14" fill="#111c31" stroke="#1e293b" />
          <text x="176" y="132" fill="#cbd5e1" fontSize="12" fontWeight="700">
            의뢰 현황
          </text>
          <rect x="176" y="144" width="72" height="64" rx="12" fill="#0c1424" stroke="#334155" />
          <text x="188" y="168" fill="#94a3b8" fontSize="9">
            진행
          </text>
          <text x="188" y="192" fill="#38bdf8" fontSize="22" fontWeight="800">
            128
          </text>
          <rect x="258" y="144" width="72" height="64" rx="12" fill="#0c1424" stroke="#334155" />
          <text x="270" y="168" fill="#94a3b8" fontSize="9">
            배정
          </text>
          <text x="270" y="192" fill="#2dd4bf" fontSize="20" fontWeight="800">
            92%
          </text>
          <rect x="340" y="144" width="72" height="64" rx="12" fill="#0c1424" stroke="#334155" />
          <text x="352" y="168" fill="#94a3b8" fontSize="9">
            알림
          </text>
          <text x="358" y="192" fill="#93c5fd" fontSize="20" fontWeight="800">
            24
          </text>

          <rect x="160" y="240" width="276" height="104" rx="14" fill="#0f1a2f" stroke="#1e293b" />
          <text x="176" y="264" fill="#cbd5e1" fontSize="11" fontWeight="700">
            진행 단계
          </text>
          <rect x="176" y="276" width="52" height="14" rx="7" fill="#1e40af" />
          <rect x="236" y="276" width="52" height="14" rx="7" fill="#0ea5e9" />
          <rect x="296" y="276" width="52" height="14" rx="7" fill="#14b8a6" />
          <rect x="356" y="276" width="52" height="14" rx="7" fill="#67e8f9" opacity="0.75" />
          <text x="176" y="318" fill="#64748b" fontSize="9">
            생성 → 배정 → 소통 → 완료
          </text>
        </g>

        {/* 플로팅 칩 */}
        {[
          { x: 468, y: 48, w: 118, h: 36, t: '의뢰 생성' },
          { x: 520, y: 100, w: 118, h: 36, t: '자동 배정' },
          { x: 488, y: 168, w: 130, h: 36, t: '진행·소통' },
          { x: 540, y: 232, w: 130, h: 36, t: '완료·보관' },
        ].map((c) => (
          <g key={c.t}>
            <rect x={c.x} y={c.y} width={c.w} height={c.h} rx="18" fill="#f8fafc" stroke="#bae6fd" strokeWidth="1.2" />
            <text x={c.x + c.w / 2} y={c.y + 23} textAnchor="middle" fill="#0f172a" fontSize="13" fontWeight="800">
              {c.t}
            </text>
          </g>
        ))}

        {/* 모바일 */}
        <g filter="url(#sgez-hero-shadow)">
          <rect x="548" y="300" width="124" height="220" rx="22" fill="#0f172a" stroke="#334155" strokeWidth="1.5" />
          <rect x="564" y="316" width="92" height="14" rx="7" fill="#1e293b" />
          <rect x="568" y="344" width="84" height="36" rx="10" fill="#111c31" stroke="#1e293b" />
          <rect x="576" y="352" width="40" height="6" rx="3" fill="#38bdf8" opacity="0.35" />
          <rect x="576" y="364" width="56" height="5" rx="2" fill="#475569" />
          <rect x="568" y="390" width="84" height="36" rx="10" fill="#111c31" stroke="#1e293b" />
          <rect x="576" y="398" width="48" height="6" rx="3" fill="#2dd4bf" opacity="0.3" />
          <rect x="576" y="410" width="52" height="5" rx="2" fill="#475569" />
          <text x="610" y="332" textAnchor="middle" fill="#94a3b8" fontSize="9" fontWeight="700">
            모바일
          </text>
          <circle cx="610" cy="472" r="16" fill="#38bdf8" opacity="0.2" />
          <path d="M604 472 L609 477 L620 464" stroke="#38bdf8" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* 연결 곡선 */}
        <path
          d="M 456 200 Q 500 180, 520 140"
          stroke="url(#sgez-hero-glow)"
          strokeWidth="2.5"
          fill="none"
          opacity="0.5"
        />
        <path
          d="M 456 260 Q 510 280, 548 340"
          stroke="url(#sgez-hero-glow)"
          strokeWidth="2.5"
          fill="none"
          opacity="0.45"
        />
      </svg>
    </MockFrame>
  )
}

/** 특장점 ①: 상담 요약·리포트형 목업 */
export function DifferentiatorHealthMockup() {
  const [rasterFailed, setRasterFailed] = React.useState(false)
  const frame =
    'w-full max-w-md overflow-hidden rounded-[24px] border border-slate-200/90 bg-white shadow-xl shadow-slate-200/60 ring-1 ring-slate-100'
  if (!rasterFailed) {
    return (
      <MockFrame ariaLabel="진료 요약과 3NN 근거가 카드로 정리된 상담 리포트 화면 목업" className={frame}>
        <img
          src={landingAssetSrc('diff-health.png')}
          alt=""
          className="h-auto w-full object-cover object-top"
          onError={() => setRasterFailed(true)}
          loading="lazy"
          decoding="async"
        />
      </MockFrame>
    )
  }
  return (
    <MockFrame
      ariaLabel="진료 요약과 3NN 근거가 카드로 정리된 상담 리포트 화면 목업"
      className={frame}
    >
      <svg viewBox="0 0 440 360" className="h-auto w-full" aria-hidden="true">
        <rect width="440" height="360" fill="#f8fafc" />
        <rect x="0" y="0" width="440" height="48" fill="#fff" stroke="#e2e8f0" />
        <text x="20" y="30" fill="#0f172a" fontSize="14" fontWeight="800">
          상담 요약 리포트
        </text>
        <rect x="320" y="14" width="100" height="28" rx="14" fill="#0ea5e9" />
        <text x="370" y="33" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="800">
          의뢰 보내기
        </text>

        <rect x="16" y="60" width="200" height="140" rx="14" fill="#fff" stroke="#e2e8f0" />
        <text x="28" y="84" fill="#64748b" fontSize="10" fontWeight="700">
          고객 요약
        </text>
        <rect x="28" y="94" width="140" height="8" rx="4" fill="#e2e8f0" />
        <rect x="28" y="110" width="120" height="8" rx="4" fill="#e2e8f0" />
        <rect x="28" y="132" width="168" height="52" rx="10" fill="#eff6ff" stroke="#bfdbfe" />
        <text x="36" y="154" fill="#1e40af" fontSize="9" fontWeight="700">
          진료 흐름
        </text>
        <text x="36" y="170" fill="#475569" fontSize="8">
          최근 방문 · 검사 요약
        </text>

        <rect x="224" y="60" width="200" height="140" rx="14" fill="#fff" stroke="#e2e8f0" />
        <text x="236" y="84" fill="#64748b" fontSize="10" fontWeight="700">
          3NN 추천 근거
        </text>
        <rect x="236" y="94" width="72" height="22" rx="8" fill="#ccfbf1" stroke="#5eead4" />
        <text x="248" y="109" fill="#0f766e" fontSize="9" fontWeight="800">
          후보 A
        </text>
        <rect x="314" y="94" width="72" height="22" rx="8" fill="#e0f2fe" stroke="#7dd3fc" />
        <text x="326" y="109" fill="#0369a1" fontSize="9" fontWeight="800">
          후보 B
        </text>
        <rect x="236" y="124" width="176" height="8" rx="4" fill="#e2e8f0" />
        <rect x="236" y="138" width="140" height="8" rx="4" fill="#e2e8f0" />
        <rect x="236" y="152" width="160" height="8" rx="4" fill="#e2e8f0" />

        <rect x="16" y="212" width="408" height="124" rx="14" fill="#fff" stroke="#e2e8f0" />
        <text x="28" y="238" fill="#64748b" fontSize="10" fontWeight="700">
          공유 요약
        </text>
        <rect x="28" y="248" width="360" height="8" rx="4" fill="#e2e8f0" />
        <rect x="28" y="264" width="320" height="8" rx="4" fill="#e2e8f0" />
        <rect x="28" y="280" width="340" height="8" rx="4" fill="#e2e8f0" />
        <rect x="28" y="304" width="120" height="20" rx="10" fill="#e0f2fe" stroke="#38bdf8" />
        <text x="48" y="318" fill="#0369a1" fontSize="9" fontWeight="700">
          매니저에게 전달
        </text>
      </svg>
    </MockFrame>
  )
}

/** 특장점 ②: 건물 + 데이터 패널 */
export function DifferentiatorFireMockup() {
  const [rasterFailed, setRasterFailed] = React.useState(false)
  const frame =
    'w-full max-w-md overflow-hidden rounded-[24px] border border-slate-200/90 bg-white shadow-xl shadow-slate-200/60 ring-1 ring-slate-100'
  if (!rasterFailed) {
    return (
      <MockFrame ariaLabel="건물 개요와 용도·면적 등 정보 패널, 화재 담보 추천이 함께 표현된 목업" className={frame}>
        <img
          src={landingAssetSrc('diff-fire.png')}
          alt=""
          className="h-auto w-full object-cover object-top"
          onError={() => setRasterFailed(true)}
          loading="lazy"
          decoding="async"
        />
      </MockFrame>
    )
  }
  return (
    <MockFrame
      ariaLabel="건물 개요와 용도·면적 등 정보 패널, 화재 담보 추천이 함께 표현된 목업"
      className={frame}
    >
      <svg viewBox="0 0 440 360" className="h-auto w-full" aria-hidden="true">
        <rect width="440" height="360" fill="#f8fafc" />
        {/* 아이소메트릭 블록 */}
        <g transform="translate(40, 100)">
          <path d="M0 80 L60 40 L120 80 L60 120 Z" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="1.2" />
          <path d="M120 80 L120 140 L60 180 L60 120 Z" fill="#94a3b8" stroke="#64748b" strokeWidth="1.2" />
          <path d="M0 80 L60 120 L60 180 L0 140 Z" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.2" />
          <rect x="40" y="60" width="40" height="36" fill="#38bdf8" opacity="0.35" rx="4" />
          <text x="48" y="118" fill="#0f172a" fontSize="11" fontWeight="800">
            건물
          </text>
        </g>

        <rect x="200" y="48" width="216" height="268" rx="16" fill="#fff" stroke="#e2e8f0" />
        <text x="216" y="76" fill="#0f172a" fontSize="13" fontWeight="800">
          건축물 정보
        </text>
        {[
          ['건물명', '○○빌딩'],
          ['용도', '근린생활시설'],
          ['연면적', '1,240㎡'],
        ].map(([k, v], i) => (
          <g key={k}>
            <text x="216" y={104 + i * 44} fill="#64748b" fontSize="10" fontWeight="600">
              {k}
            </text>
            <rect x="216" y={110 + i * 44} width="176" height="26" rx="8" fill="#f1f5f9" />
            <text x="228" y={127 + i * 44} fill="#0f172a" fontSize="11" fontWeight="700">
              {v}
            </text>
          </g>
        ))}
        <text x="216" y="238" fill="#64748b" fontSize="10" fontWeight="600">
          화재 담보 포인트
        </text>
        <rect x="216" y="246" width="168" height="40" rx="10" fill="#eff6ff" stroke="#93c5fd" />
        <text x="228" y="270" fill="#1e40af" fontSize="9" fontWeight="700">
          구조·용도 기준 추천 요약
        </text>

        <rect x="200" y="292" width="88" height="28" rx="14" fill="#ccfbf1" stroke="#2dd4bf" />
        <text x="244" y="311" textAnchor="middle" fill="#0f766e" fontSize="10" fontWeight="800">
          담보 A
        </text>
        <rect x="296" y="292" width="88" height="28" rx="14" fill="#e0f2fe" stroke="#38bdf8" />
        <text x="340" y="311" textAnchor="middle" fill="#0369a1" fontSize="10" fontWeight="800">
          담보 B
        </text>
      </svg>
    </MockFrame>
  )
}

/** 특장점 ③: 모객·청구·소식 가로 플로우 */
export function DifferentiatorLifecycleMockup() {
  const [rasterFailed, setRasterFailed] = React.useState(false)
  const frame =
    'w-full max-w-lg overflow-hidden rounded-[24px] border border-slate-200/90 bg-white shadow-xl shadow-slate-200/60 ring-1 ring-slate-100'
  if (!rasterFailed) {
    return (
      <MockFrame
        ariaLabel="명함과 랜딩에서 설문, 청구, 월간 소식까지 이어지는 과정을 단계별로 보여주는 목업"
        className={frame}
      >
        <img
          src={landingAssetSrc('diff-lifecycle.png')}
          alt=""
          className="h-auto w-full object-cover object-top"
          onError={() => setRasterFailed(true)}
          loading="lazy"
          decoding="async"
        />
      </MockFrame>
    )
  }
  const steps = [
    { label: '명함·랜딩', x: 24 },
    { label: '설문', x: 126 },
    { label: '청구', x: 228 },
    { label: '소식지', x: 330 },
  ]
  const connectors = [
    { x1: 112, tip: 126 },
    { x1: 214, tip: 228 },
    { x1: 316, tip: 330 },
  ]
  return (
    <MockFrame
      ariaLabel="명함과 랜딩에서 설문, 청구, 월간 소식까지 이어지는 과정을 단계별로 보여주는 목업"
      className={frame}
    >
      <svg viewBox="0 0 480 200" className="h-auto w-full" aria-hidden="true">
        <rect width="480" height="200" fill="#f8fafc" />
        {steps.map((s) => (
          <g key={s.label}>
            <rect x={s.x} y="36" width="88" height="100" rx="14" fill="#fff" stroke="#bae6fd" strokeWidth="1.5" />
            <rect x={s.x + 12} y="50" width="64" height="8" rx="4" fill="#e0f2fe" />
            <rect x={s.x + 12} y="66" width="48" height="6" rx="3" fill="#e2e8f0" />
            <rect x={s.x + 12} y="78" width="56" height="6" rx="3" fill="#e2e8f0" />
            <rect x={s.x + 12} y="96" width="64" height="28" rx="8" fill="#f1f5f9" />
            <text x={s.x + 44} y="158" textAnchor="middle" fill="#0f172a" fontSize="11" fontWeight="800">
              {s.label}
            </text>
          </g>
        ))}
        {connectors.map((c) => (
          <g key={`${c.x1}-${c.tip}`}>
            <line x1={c.x1} y1="86" x2={c.tip - 4} y2="86" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" />
            <polygon points={`${c.tip - 4},81 ${c.tip},86 ${c.tip - 4},91`} fill="#38bdf8" />
          </g>
        ))}
      </svg>
    </MockFrame>
  )
}

/** 웹 콘솔 — 데스크톱 프레임 */
export function LandingWebConsoleMockup() {
  const [rasterFailed, setRasterFailed] = React.useState(false)
  if (!rasterFailed) {
    return (
      <MockFrame ariaLabel="의뢰 현황과 조직 메뉴가 보이는 웹 콘솔 화면 목업" className="mt-8 w-full">
        <img
          src={landingAssetSrc('web-console.png')}
          alt=""
          className="h-auto w-full rounded-xl border border-slate-200/80 object-cover object-top shadow-inner"
          onError={() => setRasterFailed(true)}
          loading="lazy"
          decoding="async"
        />
      </MockFrame>
    )
  }
  return (
    <MockFrame ariaLabel="의뢰 현황과 조직 메뉴가 보이는 웹 콘솔 화면 목업" className="mt-8 w-full">
      <svg viewBox="0 0 520 280" className="h-auto w-full rounded-xl" aria-hidden="true">
        <rect width="520" height="280" rx="16" fill="#e2e8f0" />
        <rect x="12" y="12" width="496" height="256" rx="12" fill="#1e293b" />
        <rect x="24" y="28" width="100" height="220" rx="8" fill="#0f172a" />
        <rect x="36" y="44" width="72" height="10" rx="4" fill="#334155" />
        <rect x="36" y="64" width="72" height="8" rx="4" fill="#1e40af" opacity="0.4" />
        <rect x="36" y="80" width="56" height="8" rx="4" fill="#334155" />
        <rect x="36" y="104" width="72" height="8" rx="4" fill="#334155" />
        <rect x="36" y="120" width="64" height="8" rx="4" fill="#334155" />
        <rect x="36" y="144" width="72" height="8" rx="4" fill="#334155" />
        <text x="36" y="200" fill="#64748b" fontSize="8" fontWeight="700">
          조직·권한
        </text>
        <rect x="136" y="28" width="360" height="36" rx="8" fill="#0b1220" />
        <circle cx="152" cy="46" r="5" fill="#64748b" />
        <rect x="168" y="40" width="120" height="12" rx="4" fill="#334155" />
        <rect x="136" y="76" width="360" height="172" rx="10" fill="#0b1220" stroke="#334155" />
        <text x="152" y="104" fill="#94a3b8" fontSize="11" fontWeight="700">
          의뢰 현황
        </text>
        <rect x="152" y="116" width="100" height="56" rx="10" fill="#111c31" stroke="#1e3a8a" strokeOpacity="0.4" />
        <text x="164" y="140" fill="#64748b" fontSize="9">
          진행 중
        </text>
        <text x="164" y="162" fill="#38bdf8" fontSize="20" fontWeight="800">
          42
        </text>
        <rect x="264" y="116" width="100" height="56" rx="10" fill="#111c31" stroke="#334155" />
        <rect x="376" y="116" width="100" height="56" rx="10" fill="#111c31" stroke="#334155" />
        <rect x="152" y="188" width="328" height="44" rx="10" fill="#0f1a2f" stroke="#334155" />
        <text x="164" y="212" fill="#64748b" fontSize="9">
          공지 · 리포트 · 소식지
        </text>
      </svg>
    </MockFrame>
  )
}

/** 모바일 앱 — 단말 프레임 */
export function LandingMobileAppMockup() {
  const [rasterFailed, setRasterFailed] = React.useState(false)
  if (!rasterFailed) {
    return (
      <MockFrame ariaLabel="알림과 청구, 현장 의뢰가 보이는 모바일 앱 화면 목업" className="mt-8 flex w-full justify-center">
        <img
          src={landingAssetSrc('web-mobile.png')}
          alt=""
          className="h-auto max-h-[280px] w-full max-w-[200px] rounded-[28px] border border-slate-200 object-cover object-top shadow-lg"
          onError={() => setRasterFailed(true)}
          loading="lazy"
          decoding="async"
        />
      </MockFrame>
    )
  }
  return (
    <MockFrame ariaLabel="알림과 청구, 현장 의뢰가 보이는 모바일 앱 화면 목업" className="mt-8 flex w-full justify-center">
      <svg viewBox="0 0 200 340" className="h-auto max-h-[280px] w-auto" aria-hidden="true">
        <rect x="8" y="8" width="184" height="324" rx="28" fill="#1e293b" />
        <rect x="16" y="20" width="168" height="300" rx="20" fill="#0f172a" />
        <rect x="72" y="32" width="56" height="5" rx="2" fill="#334155" />
        <text x="100" y="62" textAnchor="middle" fill="#94a3b8" fontSize="10" fontWeight="700">
          알림
        </text>
        <rect x="28" y="72" width="144" height="44" rx="12" fill="#111c31" stroke="#334155" />
        <rect x="40" y="84" width="100" height="8" rx="4" fill="#334155" />
        <rect x="40" y="98" width="72" height="6" rx="3" fill="#1e40af" opacity="0.35" />
        <text x="100" y="138" textAnchor="middle" fill="#94a3b8" fontSize="9" fontWeight="700">
          청구 · 의뢰
        </text>
        <rect x="28" y="148" width="144" height="100" rx="12" fill="#111c31" stroke="#334155" />
        <rect x="40" y="162" width="120" height="8" rx="4" fill="#334155" />
        <rect x="40" y="178" width="96" height="8" rx="4" fill="#334155" />
        <rect x="40" y="194" width="108" height="8" rx="4" fill="#334155" />
        <rect x="28" y="260" width="144" height="44" rx="12" fill="#0c4a6e" stroke="#38bdf8" strokeOpacity="0.4" />
        <text x="100" y="288" textAnchor="middle" fill="#7dd3fc" fontSize="9" fontWeight="700">
          현장 확인
        </text>
      </svg>
    </MockFrame>
  )
}

/** 업무 흐름 — 미니 플로우 (기존과 동일 역할) */
export function WorkflowIllustration() {
  const steps = [
    { x: 26, title: '의뢰 생성', subtitle: '접수 등록', accent: '#1d4ed8' },
    { x: 152, title: '자동 배정', subtitle: '담당 연결', accent: '#0ea5e9' },
    { x: 278, title: '진행·소통', subtitle: '코멘트/첨부', accent: '#2dd4bf' },
    { x: 404, title: '완료', subtitle: '결과 확인', accent: '#38bdf8' },
    { x: 530, title: '알림', subtitle: '웹+앱 전달', accent: '#22c55e' },
  ]

  return (
    <div
      className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm transition-all duration-500 motion-safe:hover:-translate-y-1 motion-safe:hover:shadow-lg"
      role="img"
      aria-label="의뢰 생성부터 알림까지 이어지는 업무 흐름 다이어그램"
    >
      <svg viewBox="0 0 660 240" className="h-auto w-full" aria-hidden="true">
        <rect x="0" y="0" width="660" height="240" rx="28" fill="#f8fafc" />
        {steps.map((step, index) => (
          <g key={step.title}>
            <rect x={step.x} y="58" width="104" height="96" rx="22" fill="#ffffff" stroke="#cbd5e1" />
            <rect x={step.x + 18} y="78" width="68" height="10" rx="5" fill={step.accent} opacity="0.22" />
            <rect x={step.x + 18} y="98" width="42" height="8" rx="4" fill="#cbd5e1" />
            <rect x={step.x + 18} y="114" width="54" height="8" rx="4" fill="#cbd5e1" />
            <circle cx={step.x + 72} cy="121" r="14" fill={step.accent} opacity="0.18" />
            <path d={`M ${step.x + 64} 121 L ${step.x + 70} 127 L ${step.x + 82} 113`} stroke={step.accent} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <text x={step.x + 52} y="30" textAnchor="middle" fill="#0f172a" fontSize="18" fontWeight="800">{`${index + 1}. ${step.title}`}</text>
            <text x={step.x + 52} y="182" textAnchor="middle" fill="#475569" fontSize="12">
              {step.subtitle}
            </text>
          </g>
        ))}
        {[124, 250, 376, 502].map((x) => (
          <g key={x}>
            <path d={`M ${x} 106 C ${x + 18} 106, ${x + 22} 106, ${x + 34} 106`} stroke="#64748b" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d={`M ${x + 28} 100 L ${x + 36} 106 L ${x + 28} 112`} stroke="#64748b" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        ))}
      </svg>
    </div>
  )
}
